import { useEffect, useState } from 'react';
import {
  ensureServiceWorkerAwake,
  isServiceWorkerAwake,
  markServiceWorkerStale,
} from 'shared/channels/ensureServiceWorkerAwake';

/**
 * Whether this tab's service worker answers. It is pinged (which starts a
 * stopped worker) on mount, and again when a worker takes control of the page
 * (first visit, update); without an answer the views use their fallback.
 */
export function useServiceWorkerReady(): boolean {
  const [serviceWorkerReady, setServiceWorkerReady] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    const ping = (): void => {
      void ensureServiceWorkerAwake().then((result) => {
        if (mounted && isServiceWorkerAwake(result)) {
          setServiceWorkerReady(true);
        }
      });
    };
    const container = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;

    // A new controller makes an earlier "no worker" or "no answer" outdated.
    const pingNewController = (): void => {
      markServiceWorkerStale();
      ping();
    };

    ping();
    container?.addEventListener('controllerchange', pingNewController);
    return (): void => {
      mounted = false;
      container?.removeEventListener('controllerchange', pingNewController);
    };
  }, []);

  return serviceWorkerReady;
}
